// Orkestra Meetings CP2 — guest-facing video room.
//
// A PROP-DRIVEN room surface for the public /meet/:roomToken page. It mirrors
// the media/connection patterns of the staff call surfaces (InCallSurface /
// GroupInCallSurface) — Room connect, TrackSubscribed/Unsubscribed, per-tile
// video sinks, a shared hidden audio sink, mic/cam toggles — but it is
// DELIBERATELY DECOUPLED:
//   * NO useCallManager / CallManagerProvider — a guest has no app session.
//   * NO livekit-token fetch — the token + serverUrl arrive as props (minted
//     by the guest page via the meeting-guest-token fn).
//   * NO team-chat roster lookup — tile labels come straight from each LiveKit
//     participant's `name` (guests carry their display name, staff carry their
//     full name, both set server-side in their respective token fns).
// The connection code is MIRRORED, not imported, from GroupInCallSurface —
// same rationale as that file's own "COPIED, DO NOT extract" note: the proven
// staff path stays byte-stable while this public path evolves independently.
//
// Adds a screen-share toggle (the staff surfaces don't have one yet); on
// platforms that reject getDisplayMedia (most mobile browsers) the toggle
// fails soft and reverts.

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import {
  Room, RoomEvent, Track, VideoPresets,
  type RemoteParticipant, type RemoteTrackPublication, type RemoteTrack,
  type TrackPublication, type Participant, type VideoCaptureOptions,
} from 'livekit-client'
import { Mic, MicOff, Video, VideoOff, PhoneOff, MonitorUp, MonitorOff, Loader2, MessageSquare, Send, X } from 'lucide-react'
import Avatar from '@/components/Avatar'
import { initials } from '@/components/calls/CallOverlay'
import { formatDuration } from '@/components/calls/formatDuration'

const mlog = (...args: unknown[]) => { try { console.log('[meet/media]', ...args) } catch { /* ignore */ } }

interface GuestVideoRoomProps {
  token: string
  serverUrl: string
  displayName: string
  meetingTitle: string
  /** Called exactly once when the room ends (leave button, room closed,
   *  token expiry, or network drop). The parent shows its "left" card. */
  onLeave: () => void
}

interface ParticipantSlot {
  identity: string
  name: string
  hasVideo: boolean
  hasAudio: boolean
}

type ParticipantMap = Record<string, ParticipantSlot>

// Track/participant state transitions, pure + exported for unit testing.
// 'connect' seeds a slot; 'track' flips hasVideo/hasAudio (used by
// Subscribe/Unsubscribe AND Mute/Unmute — iOS mutes video aggressively, and a
// muted remote track must show a placeholder, not a frozen last frame).
export type ParticipantEvent =
  | { type: 'connect'; identity: string; name: string }
  | { type: 'disconnect'; identity: string }
  | { type: 'track'; identity: string; name: string; kind: 'video' | 'audio'; present: boolean }

export function reduceParticipants(prev: ParticipantMap, ev: ParticipantEvent): ParticipantMap {
  if (ev.type === 'disconnect') {
    const { [ev.identity]: _gone, ...rest } = prev
    return rest
  }
  if (ev.type === 'connect') {
    return prev[ev.identity] ? prev : { ...prev, [ev.identity]: { identity: ev.identity, name: ev.name, hasVideo: false, hasAudio: false } }
  }
  const cur = prev[ev.identity] ?? { identity: ev.identity, name: ev.name, hasVideo: false, hasAudio: false }
  return {
    ...prev,
    [ev.identity]: {
      ...cur,
      hasVideo: ev.kind === 'video' ? ev.present : cur.hasVideo,
      hasAudio: ev.kind === 'audio' ? ev.present : cur.hasAudio,
    },
  }
}

// Order-independent remote-video attach: only attaches when BOTH the tile
// <video> and the track exist. Called from TrackSubscribed (track arrives) AND
// from the tile's callback ref (element mounts) — either order works, which is
// the iOS late-publish fix. Mutes the element (video-only tile; remote audio is
// on the shared sink) so iOS autoplays it instead of freezing.
export function attachRemoteVideoTrack(
  el: HTMLVideoElement | null | undefined,
  track: { attach: (el: HTMLVideoElement) => void } | null | undefined,
): boolean {
  if (!el || !track) return false
  track.attach(el)
  el.autoplay = true
  el.playsInline = true
  el.muted = true
  return true
}

// Camera publish: reflect the PUBLISHED state, not intent. A rejection (iOS can
// deny/defer getUserMedia) surfaces an error instead of a silent catch that
// would leave the button "on" while nothing publishes.
export async function computeCameraToggle(
  lp: { setCameraEnabled: (b: boolean, opts?: VideoCaptureOptions) => Promise<unknown> },
  enable: boolean,
  captureOptions?: VideoCaptureOptions,
): Promise<{ camOn: boolean; error: string | null }> {
  try {
    // Pass an explicit 16:9 capture preset on enable so publishers don't send a
    // native (often 4:3 / portrait) source that the tiles then crop.
    await lp.setCameraEnabled(enable, enable ? captureOptions : undefined)
    return { camOn: enable, error: null }
  } catch {
    return { camOn: false, error: 'Camera couldn’t start — check permissions and try again.' }
  }
}

// Initial camera enable after connect, with ONE retry. iOS can reject
// getUserMedia when the Join-tap's user-gesture context has lapsed by the time
// the room connection resolves (which is exactly why a later manual tap works).
// A single retry ~1s later usually lands; on final failure the copy tells the
// guest to tap the button (a fresh gesture). sleep is injected for testing.
export async function computeInitialCamera(
  lp: { setCameraEnabled: (b: boolean, opts?: VideoCaptureOptions) => Promise<unknown> },
  captureOptions: VideoCaptureOptions | undefined,
  retryDelayMs: number,
  sleep: (ms: number) => Promise<void>,
): Promise<{ camOn: boolean; error: string | null; attempts: number }> {
  const first = await computeCameraToggle(lp, true, captureOptions)
  if (first.camOn) return { camOn: true, error: null, attempts: 1 }
  await sleep(retryDelayMs)
  const second = await computeCameraToggle(lp, true, captureOptions)
  if (second.camOn) return { camOn: true, error: null, attempts: 2 }
  return { camOn: false, error: 'Camera couldn’t start — tap the camera button.', attempts: 2 }
}

// Meet-room responsive CSS (inline styles can't express @media / count-aware
// columns). Rides a <style> tag; !important overrides inline paddings.
//   * Count-aware grid: portrait 1col(≤2)/2col(3+); landscape+desktop
//     2col(≤4)/3col(5+). Rows flow (each tile is 16:9), the scroll container
//     scrolls on overflow — tiles cannot overlap by construction.
//   * Short-viewport landscape: header collapses to a strip (subline hidden),
//     controls + scroll padding tighten, the flex:1 stage reclaims height.
// Portrait defaults untouched. Tokens only.
// Remote-tile count → grid bucket class. CSS (MEET_CSS) maps each bucket to a
// column count per viewport. Exported for unit assertion.
export function gridBucketFor(remoteCount: number): 'gvr-few' | 'gvr-mid' | 'gvr-many' {
  return remoteCount <= 2 ? 'gvr-few' : remoteCount <= 4 ? 'gvr-mid' : 'gvr-many'
}

export const MEET_CSS = `
.gvr-grid { display: grid; gap: 12px; grid-template-columns: 1fr; }
.gvr-grid.gvr-mid { grid-template-columns: 1fr 1fr; }
.gvr-grid.gvr-many { grid-template-columns: 1fr 1fr; }
@media (min-width: 700px), (orientation: landscape) {
  .gvr-grid.gvr-few { grid-template-columns: 1fr 1fr; }
  .gvr-grid.gvr-mid { grid-template-columns: 1fr 1fr; }
  .gvr-grid.gvr-many { grid-template-columns: 1fr 1fr 1fr; }
}
.gvr-chat { position: absolute; inset: 0; }
@media (min-width: 700px) {
  .gvr-chat { left: auto !important; width: 360px !important; max-width: 90vw !important; }
}
@media (orientation: landscape) and (max-height: 500px) {
  .gvr-header { padding: 4px 12px !important; }
  .gvr-subline { display: none !important; }
  .gvr-scroll { padding: 6px !important; }
  .gvr-controls { padding: 6px 12px calc(6px + env(safe-area-inset-bottom)) !important; }
}
`

// Room lifecycle wiring, extracted so it's unit-testable with a fake room
// (no jsdom / media stack needed). Reconnecting/Reconnected drive the
// non-blocking indicator; only a FINAL Disconnected is terminal.
export function attachGuestRoomLifecycle(
  room: Pick<Room, 'on'>,
  handlers: { onReconnecting: () => void; onReconnected: () => void; onDisconnected: () => void },
): void {
  room.on(RoomEvent.Reconnecting, handlers.onReconnecting)
  room.on(RoomEvent.Reconnected, handlers.onReconnected)
  room.on(RoomEvent.Disconnected, handlers.onDisconnected)
}

// ─── In-meeting ephemeral chat (LiveKit data channel, CP4) ─────────────────
// No persistence, no backend — messages ride the data channel and vanish with
// the session. All helpers are pure + exported for unit testing.
export const CHAT_MAX_MESSAGES = 200
export const CHAT_MAX_TEXT = 2000

export interface ChatEnvelope { v: number; type: 'chat'; name: string; text: string; ts: number }
export interface ChatMessage { id: string; name: string; text: string; ts: number; mine: boolean }

export function makeChatEnvelope(name: string, text: string, ts: number): ChatEnvelope {
  return { v: 1, type: 'chat', name, text: text.slice(0, CHAT_MAX_TEXT), ts }
}

export function encodeChat(env: ChatEnvelope): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(env))
}

// Strip our server-derived guest identity ('guest_<slug>_<rand>') back to a
// human-ish name; staff identities (bare UUIDs) and anything else pass through.
export function stripGuestPrefix(identity: string): string {
  const m = /^guest_(.+)_[a-z0-9]{6}$/.exec(identity)
  return m ? m[1].replace(/-/g, ' ') : identity
}

// Parse an incoming payload into a ChatMessage, or null if it isn't a valid
// chat envelope — junk / non-chat / future types are ignored SILENTLY.
export function decodeChatMessage(payload: Uint8Array, fallbackName: string, ts: number): ChatMessage | null {
  let obj: unknown
  try { obj = JSON.parse(new TextDecoder().decode(payload)) } catch { return null }
  if (!obj || typeof obj !== 'object') return null
  const o = obj as Record<string, unknown>
  if (o.type !== 'chat' || typeof o.text !== 'string') return null
  const name = (typeof o.name === 'string' && o.name.trim()) ? (o.name as string) : fallbackName
  const msgTs = typeof o.ts === 'number' ? (o.ts as number) : ts
  return { id: `${msgTs}-${Math.round(msgTs % 100000)}-${name}`, name, text: (o.text as string).slice(0, CHAT_MAX_TEXT), ts: msgTs, mine: false }
}

// Append with a hard cap — oldest evicted so memory can't grow unbounded.
export function appendChat(list: ChatMessage[], msg: ChatMessage, cap = CHAT_MAX_MESSAGES): ChatMessage[] {
  const next = [...list, msg]
  return next.length > cap ? next.slice(next.length - cap) : next
}

// Unread badge: incoming while closed increments; while open it stays cleared.
export function bumpUnread(current: number, panelOpen: boolean): number {
  return panelOpen ? 0 : current + 1
}

export async function publishChat(
  lp: { publishData: (data: Uint8Array, opts?: { reliable?: boolean }) => Promise<void> },
  env: ChatEnvelope,
): Promise<void> {
  await lp.publishData(encodeChat(env), { reliable: true })
}

export default function GuestVideoRoom({
  token, serverUrl, displayName, meetingTitle, onLeave,
}: GuestVideoRoomProps) {
  const roomRef = useRef<Room | null>(null)
  const [connected, setConnected] = useState(false)
  const [micOn, setMicOn] = useState(true)
  const [camOn, setCamOn] = useState(false)
  const [screenOn, setScreenOn] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [leaving, setLeaving] = useState(false)

  const [participants, setParticipants] = useState<Record<string, ParticipantSlot>>({})

  const localVideoRef = useRef<HTMLVideoElement | null>(null)
  const tileVideoRefs = useRef<Map<string, HTMLVideoElement>>(new Map())
  const remoteAudioContainerRef = useRef<HTMLDivElement | null>(null)

  // Transient-reconnect indicator (backgrounded tab / network blip). Shown
  // non-blocking over the room; NOT a leave.
  const [reconnecting, setReconnecting] = useState(false)

  // ─── Ephemeral chat (CP4) ─────────────────────────────────────────────────
  const [chatOpen, setChatOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [chatText, setChatText] = useState('')
  const [unread, setUnread] = useState(0)
  // Read the OPEN state inside the once-registered DataReceived handler.
  const chatOpenRef = useRef(false)
  useEffect(() => { chatOpenRef.current = chatOpen }, [chatOpen])
  const chatScrollRef = useRef<HTMLDivElement | null>(null)
  const stickBottomRef = useRef(true)   // autoscroll unless the user scrolled up

  // Keep the latest onLeave in a ref so `finish` — and the connect effect that
  // depends on it — stays STABLE across parent re-renders. Without this, an
  // inline onLeave prop (recreated every MeetJoin render) changed `finish`'s
  // identity, re-ran the connect effect, and its cleanup disconnected the live
  // room. That was the "tab switch drops the call" bug: any re-render tore the
  // room down, and the resulting Disconnected bounced the guest to the card.
  const onLeaveRef = useRef(onLeave)
  useEffect(() => { onLeaveRef.current = onLeave }, [onLeave])

  // onLeave must fire exactly once — the leave button, the FINAL Disconnected
  // event, and unmount cleanup can all race; this ref collapses them to one.
  const endedRef = useRef(false)
  const finish = useCallback(() => {
    if (endedRef.current) return
    endedRef.current = true
    onLeaveRef.current()
  }, [])

  // Subscribed remote video tracks by identity — held so the tile can attach
  // whenever its <video> mounts, independent of when the track arrived.
  const remoteVideoTracks = useRef<Map<string, RemoteTrack>>(new Map())
  const attachRemoteVideo = useCallback((identity: string) => {
    attachRemoteVideoTrack(tileVideoRefs.current.get(identity), remoteVideoTracks.current.get(identity))
  }, [])

  const setTileVideoRef = useCallback((identity: string) => (el: HTMLVideoElement | null) => {
    if (el) { tileVideoRefs.current.set(identity, el); attachRemoteVideo(identity) }
    else tileVideoRefs.current.delete(identity)
  }, [attachRemoteVideo])

  // Attach the local camera track to the self-view PiP once it's published.
  const attachLocalCamera = useCallback(() => {
    const r = roomRef.current
    if (!r) return
    window.setTimeout(() => {
      const pub = r.localParticipant.getTrackPublication(Track.Source.Camera)
      const track = pub?.track
      if (track && localVideoRef.current) track.attach(localVideoRef.current)
    }, 50)
  }, [])

  // ─── Duration timer (identical to the staff surfaces) ─────────────────────
  const [callStartedAt, setCallStartedAt] = useState<number | null>(null)
  const [elapsedSec, setElapsedSec] = useState(0)
  useEffect(() => {
    if (connected && callStartedAt === null) setCallStartedAt(Date.now())
  }, [connected, callStartedAt])
  useEffect(() => {
    if (callStartedAt === null) return
    const tick = () => setElapsedSec(Math.floor((Date.now() - callStartedAt) / 1000))
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [callStartedAt])

  // ─── LiveKit connection (MIRRORS GroupInCallSurface — token is a prop) ────
  useEffect(() => {
    if (!token || !serverUrl) return
    let cancelled = false

    const join = async () => {
      mlog('connecting guest room')
      try {
        const room = new Room({})
        roomRef.current = room

        room.on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
          setParticipants(prev => reduceParticipants(prev, { type: 'connect', identity: p.identity, name: p.name || 'Participant' }))
        })

        room.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
          tileVideoRefs.current.delete(p.identity)
          remoteVideoTracks.current.delete(p.identity)
          setParticipants(prev => reduceParticipants(prev, { type: 'disconnect', identity: p.identity }))
        })

        room.on(RoomEvent.TrackSubscribed, (
          track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant,
        ) => {
          const name = participant.name || 'Participant'
          if (track.kind === Track.Kind.Video) {
            // Hold the track and attach reactively — the tile <video> may not be
            // mounted yet (iOS late-publish). attachRemoteVideo fires here if the
            // element exists, and again from the tile's ref when it mounts.
            remoteVideoTracks.current.set(participant.identity, track)
            setParticipants(prev => reduceParticipants(prev, { type: 'track', identity: participant.identity, name, kind: 'video', present: true }))
            attachRemoteVideo(participant.identity)
          } else {
            const el = track.attach()
            el.id = `remote-audio-${participant.identity}-${track.sid}`
            el.autoplay = true
            el.style.cssText = 'display:none'
            remoteAudioContainerRef.current?.appendChild(el)
            setParticipants(prev => reduceParticipants(prev, { type: 'track', identity: participant.identity, name, kind: 'audio', present: true }))
          }
        })

        room.on(RoomEvent.TrackUnsubscribed, (
          track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant,
        ) => {
          track.detach().forEach(el => el.remove())
          if (track.kind === Track.Kind.Video) remoteVideoTracks.current.delete(participant.identity)
          const kind = track.kind === Track.Kind.Video ? 'video' : 'audio'
          setParticipants(prev => prev[participant.identity]
            ? reduceParticipants(prev, { type: 'track', identity: participant.identity, name: participant.name || 'Participant', kind, present: false })
            : prev)
        })

        // iOS mutes video aggressively (backgrounding, thermal). A muted remote
        // video -> placeholder (not a frozen frame); unmute re-attaches.
        room.on(RoomEvent.TrackMuted, (pub: TrackPublication, participant: Participant) => {
          if (pub.kind !== Track.Kind.Video) return
          setParticipants(prev => prev[participant.identity]
            ? reduceParticipants(prev, { type: 'track', identity: participant.identity, name: participant.name || 'Participant', kind: 'video', present: false })
            : prev)
        })
        room.on(RoomEvent.TrackUnmuted, (pub: TrackPublication, participant: Participant) => {
          if (pub.kind !== Track.Kind.Video) return
          setParticipants(prev => prev[participant.identity]
            ? reduceParticipants(prev, { type: 'track', identity: participant.identity, name: participant.name || 'Participant', kind: 'video', present: true })
            : prev)
          attachRemoteVideo(participant.identity)
        })

        // Ride LiveKit's built-in reconnection: transient drops (backgrounded
        // tab, network blip) surface as Reconnecting -> Reconnected and must
        // NOT end the call — only a non-blocking indicator shows. A FINAL
        // Disconnected (reconnect exhausted, room closed, or explicit leave) is
        // terminal and calls onLeave. Backgrounded media may be throttled by
        // the browser; we do NOT unpublish — LiveKit resumes on refocus.
        attachGuestRoomLifecycle(room, {
          onReconnecting: () => { mlog('reconnecting'); setReconnecting(true) },
          onReconnected:  () => { mlog('reconnected'); setReconnecting(false) },
          onDisconnected: () => { mlog('disconnected (final)'); finish() },
        })

        // Ephemeral chat over the data channel (CP4). Junk / non-chat payloads
        // are dropped silently inside decodeChatMessage.
        room.on(RoomEvent.DataReceived, (payload: Uint8Array, participant?: RemoteParticipant) => {
          const fallback = stripGuestPrefix(participant?.identity ?? 'Guest')
          const msg = decodeChatMessage(payload, fallback, Date.now())
          if (!msg) return
          setMessages(prev => appendChat(prev, msg))
          setUnread(u => bumpUnread(u, chatOpenRef.current))
        })

        await room.connect(serverUrl, token)
        mlog('connected')

        // Seed from anyone already present (they don't fire ParticipantConnected).
        const seed: Record<string, ParticipantSlot> = {}
        room.remoteParticipants.forEach((p) => {
          seed[p.identity] = { identity: p.identity, name: p.name || 'Participant', hasVideo: false, hasAudio: false }
        })
        if (!cancelled) setParticipants(seed)

        // Mic on (hardened, caught locally so a mic failure isn't misreported
        // as a connection failure by the outer catch).
        try {
          await room.localParticipant.setMicrophoneEnabled(true)
          if (!cancelled) setMicOn(true)
        } catch {
          if (!cancelled) { setMicOn(false); setErr('Microphone couldn’t start — tap the mic button.') }
        }

        if (!cancelled) setConnected(true)

        // Camera on automatically AFTER connect, through the same hardened path
        // as the manual toggle (h720, error-surfaced), with one retry for iOS.
        if (!cancelled) {
          const res = await computeInitialCamera(
            room.localParticipant,
            { resolution: VideoPresets.h720.resolution },
            1000,
            (ms) => new Promise<void>((r) => window.setTimeout(r, ms)),
          )
          if (!cancelled) {
            setCamOn(res.camOn)
            if (res.error) setErr(res.error)
            else if (res.camOn) attachLocalCamera()
          }
        }
      } catch (e) {
        console.error('[meet/media] connect failed:', e)
        if (!cancelled) setErr('We couldn’t connect you to the meeting. Please try again.')
      }
    }

    join()
    return () => {
      cancelled = true
      const r = roomRef.current
      if (r) r.disconnect().catch(() => { /* ignore */ })
      roomRef.current = null
    }
  }, [token, serverUrl, finish, attachLocalCamera])

  const toggleMic = async () => {
    const r = roomRef.current
    if (!r) return
    const next = !micOn
    setMicOn(next)
    await r.localParticipant.setMicrophoneEnabled(next)
  }

  const toggleCam = async () => {
    const r = roomRef.current
    if (!r) return
    const next = !camOn
    const { camOn: published, error } = await computeCameraToggle(
      r.localParticipant, next, { resolution: VideoPresets.h720.resolution },
    )
    setCamOn(published)               // reflect the PUBLISHED state, not intent
    if (error) { setErr(error); return }
    if (published) attachLocalCamera()
    else if (localVideoRef.current) localVideoRef.current.srcObject = null
  }

  const toggleScreen = async () => {
    const r = roomRef.current
    if (!r) return
    const next = !screenOn
    try {
      await r.localParticipant.setScreenShareEnabled(next)
      setScreenOn(next)
    } catch (e) {
      // getDisplayMedia is unsupported / denied (common on mobile). Fail soft.
      mlog('screen share toggle failed', e)
      setScreenOn(false)
    }
  }

  const handleLeave = () => {
    if (leaving) return
    setLeaving(true)
    const r = roomRef.current
    if (r) {
      // disconnect() fires RoomEvent.Disconnected -> finish(); belt-and-braces
      // finish() below in case the event doesn't arrive.
      r.disconnect().catch(() => { /* ignore */ })
    }
    finish()
  }

  const sendChat = async () => {
    const r = roomRef.current
    const text = chatText.trim()
    if (!r || !text) return
    const env = makeChatEnvelope(displayName, text, Date.now())
    setChatText('')
    // LiveKit doesn't echo our own data back — append locally.
    setMessages(prev => appendChat(prev, { id: `${env.ts}-me`, name: displayName, text: env.text, ts: env.ts, mine: true }))
    stickBottomRef.current = true
    try { await publishChat(r.localParticipant, env) } catch { setErr('Message failed to send.') }
  }

  const toggleChat = () => {
    setChatOpen(o => {
      const next = !o
      if (next) setUnread(0)   // clear the badge on open
      return next
    })
  }

  const onChatScroll = () => {
    const el = chatScrollRef.current
    if (!el) return
    // Stay pinned to the newest message unless the user has scrolled up.
    stickBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
  }

  // Autoscroll to newest when stuck to bottom.
  useEffect(() => {
    if (chatOpen && stickBottomRef.current && chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight
    }
  }, [messages, chatOpen])

  const fmtTime = (ts: number) => {
    const d = new Date(ts)
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  }

  const headerStatus = connected ? formatDuration(elapsedSec) : 'Connecting…'
  const remoteCount = Object.keys(participants).length
  // Count bucket → CSS picks columns per viewport (see MEET_CSS): portrait
  // 1col(≤2)/2col(3+); landscape+desktop 2col(≤4)/3col(5+). Beyond → scroll.
  const gridBucket = gridBucketFor(remoteCount)

  return (
    <>
      <style>{MEET_CSS}</style>
      <div style={{
        position: 'fixed', inset: 0,
        background: 'var(--call-stage-bg)',
        display: 'flex', flexDirection: 'column',
        zIndex: 500,
        color: 'var(--on-scrim-light-ink)',
      }}>
        {/* Header — meeting title + participant count + timer */}
        <div className="gvr-header" style={{
          padding: 'calc(12px + env(safe-area-inset-top)) 16px 12px',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{ flex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <p style={{
              fontSize: 15, fontWeight: 700, color: 'var(--on-scrim-light-ink)',
              margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {meetingTitle}
            </p>
            <p className="gvr-subline" style={{
              fontSize: 12, color: 'var(--on-scrim-light-ink)', opacity: 0.7, margin: 0,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {remoteCount + 1} participant{remoteCount + 1 === 1 ? '' : 's'} · {headerStatus}
            </p>
          </div>
        </div>

        {/* Stage — a vertically-scrolling, count-aware tile grid, or a waiting
            message when alone. The grid FLOWS (auto rows sized by each tile's
            16:9 aspect) inside a scroll container, so tiles can never overlap —
            overflow scrolls instead of collapsing/colliding fixed tracks. */}
        <div className="gvr-stage" style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {remoteCount === 0 ? (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 8, color: 'var(--on-scrim-light-ink)',
            }}>
              <p style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Waiting for others to join…</p>
              <p style={{ fontSize: 13, opacity: 0.7, margin: 0 }}>{headerStatus}</p>
            </div>
          ) : (
            <div className="gvr-scroll" style={{ position: 'absolute', inset: 0, overflowY: 'auto', padding: 12 }}>
              <div className={`gvr-grid ${gridBucket}`} style={{ display: 'grid', gap: 12 }}>
                {Object.values(participants).map(p => (
                  <ParticipantTile key={p.identity} participant={p} videoRef={setTileVideoRef(p.identity)} />
                ))}
              </div>
            </div>
          )}

          {/* Self-view PiP — floating, only when the camera is on */}
          <div style={{
            position: 'absolute', bottom: 16, right: 16,
            width: 'min(180px, 30vw)', aspectRatio: '3 / 4',
            borderRadius: 12, overflow: 'hidden',
            border: '1px solid var(--call-stage-border-subtle)',
            boxShadow: 'var(--shadow-popover-mid)',
            background: 'var(--call-stage-control-bg)',
            display: camOn ? 'block' : 'none', zIndex: 2,
          }}>
            <video ref={localVideoRef} muted autoPlay playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)', display: 'block' }} />
          </div>

          {err && (
            <div style={{
              position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
              background: 'var(--danger)', color: 'var(--ink-inverse)',
              padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, zIndex: 3,
              maxWidth: 'calc(100% - 24px)', textAlign: 'center',
            }}>
              {err}
            </div>
          )}

          {/* Non-blocking reconnect indicator — the call is NOT over, LiveKit
              is riding out a transient drop (e.g. this tab was backgrounded). */}
          {reconnecting && !err && (
            <div role="status" style={{
              position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
              background: 'var(--call-stage-control-bg)', color: 'var(--on-scrim-light-ink)',
              border: '1px solid var(--call-stage-border-subtle)',
              padding: '8px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600, zIndex: 3,
              display: 'inline-flex', alignItems: 'center', gap: 8, pointerEvents: 'none',
            }}>
              <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
              Reconnecting…
            </div>
          )}

          {/* Ephemeral chat panel (CP4). Overlays the stage; right-docked on
              wide viewports via MEET_CSS. Text is rendered as textContent
              ({m.text}) — never innerHTML. */}
          {chatOpen && (
            <div className="gvr-chat" style={{
              zIndex: 5, background: 'var(--surface-raised)',
              borderLeft: '1px solid var(--border-subtle)',
              display: 'flex', flexDirection: 'column',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)' }}>Chat</span>
                <button type="button" onClick={toggleChat} aria-label="Close chat" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', padding: 2 }}>
                  <X style={{ width: 18, height: 18 }} />
                </button>
              </div>
              <p style={{ margin: 0, padding: '8px 14px', fontSize: 11, lineHeight: 1.4, color: 'var(--ink-tertiary)', borderBottom: '1px solid var(--border-subtle)' }}>
                Chat is only visible to people in this meeting and isn’t saved.
              </p>
              <div ref={chatScrollRef} onScroll={onChatScroll} style={{ flex: 1, overflowY: 'auto', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {messages.map(m => (
                  <div key={m.id} style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: m.mine ? 'flex-end' : 'flex-start' }}>
                    <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{m.mine ? 'You' : m.name} · {fmtTime(m.ts)}</span>
                    <span style={{ fontSize: 13, color: 'var(--ink-primary)', background: m.mine ? 'var(--accent-dim)' : 'var(--surface-sunken)', padding: '6px 10px', borderRadius: 10, maxWidth: '85%', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{m.text}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, padding: '10px 14px', borderTop: '1px solid var(--border-subtle)' }}>
                <textarea value={chatText} maxLength={CHAT_MAX_TEXT} rows={1}
                  onChange={e => setChatText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void sendChat() } }}
                  placeholder="Message…"
                  style={{ flex: 1, resize: 'none', padding: '8px 10px', fontSize: 13, background: 'var(--field-bg)', color: 'var(--ink-primary)', border: '1px solid var(--border-default)', borderRadius: 8, outline: 'none', boxSizing: 'border-box' }} />
                <button type="button" onClick={() => void sendChat()} aria-label="Send message" disabled={!chatText.trim()}
                  style={{ flexShrink: 0, padding: '0 12px', background: 'var(--accent)', color: 'var(--accent-ink)', border: 'none', borderRadius: 8, cursor: chatText.trim() ? 'pointer' : 'default', opacity: chatText.trim() ? 1 : 0.5, display: 'inline-flex', alignItems: 'center' }}>
                  <Send style={{ width: 16, height: 16 }} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Control pill — mic / camera / screen / chat / leave */}
        <div className="gvr-controls" style={{ padding: '12px 16px calc(20px + env(safe-area-inset-bottom))', display: 'flex', justifyContent: 'center' }}>
          <div style={{
            display: 'inline-flex', gap: 12, padding: '8px 14px',
            background: 'var(--call-stage-control-bg)',
            border: '1px solid var(--call-stage-border-subtle)',
            borderRadius: 999, backdropFilter: 'blur(10px)', boxShadow: 'var(--shadow-popover-mid)',
            maxWidth: '100%', flexWrap: 'wrap', justifyContent: 'center',
          }}>
            <button type="button" onClick={toggleMic} aria-label={micOn ? 'Mute mic' : 'Unmute mic'}
              style={ctrlStyle(micOn ? 'var(--call-stage-control-bg-active)' : 'var(--danger)', micOn ? 'var(--on-scrim-light-ink)' : 'var(--ink-inverse)')}>
              {micOn ? <Mic style={ICON} /> : <MicOff style={ICON} />}
            </button>
            <button type="button" onClick={toggleCam} aria-label={camOn ? 'Stop camera' : 'Start camera'}
              style={ctrlStyle(camOn ? 'var(--accent)' : 'var(--call-stage-control-bg-active)', camOn ? 'var(--accent-ink)' : 'var(--on-scrim-light-ink)')}>
              {camOn ? <Video style={ICON} /> : <VideoOff style={ICON} />}
            </button>
            <button type="button" onClick={toggleScreen} aria-label={screenOn ? 'Stop sharing screen' : 'Share screen'}
              style={ctrlStyle(screenOn ? 'var(--accent)' : 'var(--call-stage-control-bg-active)', screenOn ? 'var(--accent-ink)' : 'var(--on-scrim-light-ink)')}>
              {screenOn ? <MonitorOff style={ICON} /> : <MonitorUp style={ICON} />}
            </button>
            <button type="button" onClick={toggleChat} aria-label={chatOpen ? 'Close chat' : 'Open chat'}
              style={{ ...ctrlStyle(chatOpen ? 'var(--accent)' : 'var(--call-stage-control-bg-active)', chatOpen ? 'var(--accent-ink)' : 'var(--on-scrim-light-ink)'), position: 'relative' }}>
              <MessageSquare style={ICON} />
              {unread > 0 && !chatOpen && (
                <span aria-label={`${unread} unread`} style={{ position: 'absolute', top: -2, right: -2, minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: 'var(--danger)', color: 'var(--ink-inverse)', fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </button>
            <button type="button" onClick={handleLeave} disabled={leaving} aria-label="Leave meeting"
              style={{ ...ctrlStyle('var(--danger)', 'var(--ink-inverse)'), opacity: leaving ? 0.6 : 1, cursor: leaving ? 'default' : 'pointer' }}>
              <PhoneOff style={ICON} />
            </button>
          </div>
        </div>
      </div>

      {/* Hidden audio sink — outside the stage div for always-alive intent */}
      <div ref={remoteAudioContainerRef} style={{ display: 'none' }} />
    </>
  )
}

const ICON = { width: 22, height: 22 } as const
function ctrlStyle(bg: string, color: string): CSSProperties {
  return {
    minWidth: 52, minHeight: 52, borderRadius: 999, padding: 0,
    background: bg, color, border: 'none', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  }
}

// Tile styles exported for unit assertion. Remote video is object-fit CONTAIN
// (no crop / "zoom") and letterboxes against a TOKEN background; the tile holds
// a 16:9 aspect so contain doesn't collapse heights.
export const REMOTE_TILE_STYLE: CSSProperties = {
  position: 'relative', borderRadius: 12, overflow: 'hidden',
  background: 'var(--call-stage-control-bg)',
  border: '1px solid var(--call-stage-border-subtle)',
  // 16:9 only — height derives from the cell width. NO fixed/min height: a
  // fixed height fighting the aspect + row tracks is what let tiles overlap.
  aspectRatio: '16 / 9',
}
export const REMOTE_VIDEO_STYLE: CSSProperties = {
  width: '100%', height: '100%', objectFit: 'contain',
  background: 'var(--call-stage-bg)',   // letterbox bars — token, not raw hex
}

// ─── Per-participant tile (labels from LiveKit participant.name) ────────────
function ParticipantTile({
  participant, videoRef,
}: {
  participant: ParticipantSlot
  videoRef: (el: HTMLVideoElement | null) => void
}) {
  const name = participant.name
  return (
    <div style={REMOTE_TILE_STYLE}>
      <video ref={videoRef} autoPlay playsInline muted
        style={{ ...REMOTE_VIDEO_STYLE, display: participant.hasVideo ? 'block' : 'none' }} />
      {!participant.hasVideo && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 8, padding: 16,
        }}>
          <div style={{ borderRadius: '50%', overflow: 'hidden' }}>
            <Avatar name={name} size={64} variant="contact" />
          </div>
          <p aria-hidden="true" style={{
            fontSize: 10, fontWeight: 700, color: 'var(--on-scrim-light-ink)',
            opacity: 0.6, letterSpacing: '0.06em', textTransform: 'uppercase', margin: 0,
          }}>
            {initials(name)}
          </p>
        </div>
      )}
      <div style={{
        position: 'absolute', bottom: 8, left: 8,
        background: 'var(--call-stage-bg)', color: 'var(--on-scrim-light-ink)',
        border: '1px solid var(--call-stage-border-subtle)', borderRadius: 8,
        padding: '3px 8px', fontSize: 11, fontWeight: 600,
        maxWidth: 'calc(100% - 16px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {name}
      </div>
      {!participant.hasAudio && (
        <div aria-hidden="true" style={{
          position: 'absolute', top: 8, right: 8,
          background: 'var(--call-stage-bg)', color: 'var(--on-scrim-light-ink)',
          border: '1px solid var(--call-stage-border-subtle)', borderRadius: '50%',
          width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', opacity: 0.85,
        }}>
          <MicOff style={{ width: 12, height: 12 }} />
        </div>
      )}
    </div>
  )
}
